import { useState } from "react";
import { AnimatePresence, motion, MotionConfig } from "framer-motion";
import { UsersRound } from "lucide-react";
import { BackdropGate } from "./components/background/BackdropGate";
import { AuthScreen } from "./components/screens/AuthScreen";
import { GameScreen } from "./components/screens/GameScreen";
import { HomeScreen } from "./components/screens/HomeScreen";
import { LobbyScreen } from "./components/screens/LobbyScreen";
import { ProfileScreen } from "./components/screens/ProfileScreen";
import { FriendsPanel, type FriendsTab } from "./components/social/FriendsPanel";
import { InviteToast } from "./components/social/InviteToast";
import { PwaInstallPrompt } from "./components/pwa/PwaInstallPrompt";
import { ErrorToast } from "./components/ui/ErrorToast";
import { LoadingView } from "./components/ui/LoadingView";
import { UpdateBanner } from "./components/ui/UpdateBanner";
import { useAppUpdate } from "./hooks/useAppUpdate";
import { useAuth } from "./hooks/useAuth";
import { useSkyjoGame } from "./hooks/useSkyjoGame";
import { socialErrorMessage, useSocial } from "./hooks/useSocial";
import type { GameInvite } from "./types/protocol";

export default function App() {
  const client = useSkyjoGame();
  const auth = useAuth();
  const social = useSocial();
  const [authScreenOpen, setAuthScreenOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [friendsOpen, setFriendsOpen] = useState(false);
  const [friendsTab, setFriendsTab] = useState<FriendsTab>("friends");
  // Recharger au milieu d'un tour ferait perdre la main : hors table seulement.
  const update = useAppUpdate({ safeToReload: client.room === null && !client.isRestoring });
  // L'écran de compte n'est jamais un passage obligé : il s'efface dès qu'une table existe.
  const showAuthScreen = authScreenOpen && auth.accountsEnabled && !client.room;
  const showProfileScreen = profileOpen && auth.user !== null && !client.room;
  const selfPlayer = client.room?.players.find((player) => player.id === client.room?.selfPlayerId);
  // Inviter n'a de sens que pour l'hôte d'un salon qui attend encore du monde.
  const canInvite = Boolean(
    selfPlayer?.isHost &&
    client.room?.status === "lobby" &&
    client.room.players.length < client.room.maxPlayers,
  );

  // Le tiroir des amis affiche déjà ses erreurs : partout ailleurs (bandeau
  // d'invitation compris), le message global prend le relais.
  const socialFailure =
    friendsOpen || social.error === null
      ? null
      : { ...social.error, message: socialErrorMessage(social.error) ?? social.error.message };

  const openFriends = (tab: FriendsTab) => {
    setFriendsTab(tab);
    setFriendsOpen(true);
  };

  // Le serveur refuse toute jointure depuis une table déjà occupée : on libère
  // la place d'abord, sinon le joueur ne récolterait qu'une erreur.
  const joinFriendRoom = async (roomCode: string) => {
    setFriendsOpen(false);
    if (client.room) await client.leaveRoom();
    await client.joinRoom(roomCode, auth.user?.displayName ?? client.preferredName);
  };

  // Le serveur refuse d'asseoir une connexion déjà attablée : on libère la place
  // avant d'accepter. Quitter une partie en cours pour cela y renonce pour de
  // bon — c'est ce qu'annonce le bandeau d'invitation.
  const acceptInvite = async (invite: GameInvite) => {
    if (client.room) await client.leaveRoom();
    const joined = await social.respondToInvite(invite.id, true);
    if (!joined) return;
    setFriendsOpen(false);
    setProfileOpen(false);
    client.acceptInvite(joined);
  };

  const screen = client.isRestoring && !client.room ? (
    <LoadingView key="restore" />
  ) : showAuthScreen ? (
    <AuthScreen key="auth" onClose={() => setAuthScreenOpen(false)} />
  ) : showProfileScreen ? (
    <ProfileScreen key="profile" onClose={() => setProfileOpen(false)} />
  ) : !client.room ? (
    <HomeScreen
      key="home"
      connection={client.connection}
      pendingAction={client.pendingAction}
      preferredName={client.preferredName}
      onCreate={client.createRoom}
      onJoin={client.joinRoom}
      onOpenAuth={() => setAuthScreenOpen(true)}
      onOpenProfile={auth.user ? () => setProfileOpen(true) : undefined}
    />
  ) : client.room.status === "lobby" ? (
    <LobbyScreen
      key="lobby"
      room={client.room}
      connection={client.connection}
      pendingAction={client.pendingAction}
      social={social}
      onStart={client.startGame}
      onLeave={client.goHome}
      onCloseTable={client.closeRoom}
      onOpenFriends={() => openFriends("friends")}
    />
  ) : client.game ? (
    <GameScreen
      key="game"
      game={client.game}
      connection={client.connection}
      pendingAction={client.pendingAction}
      onDraw={client.draw}
      onSwap={client.swap}
      onDiscardDrawn={client.discardDrawn}
      onReclaimDiscard={client.reclaimDiscard}
      onRevealAfterDiscard={client.revealAfterDiscard}
      onRevealInitial={client.revealInitial}
      onNextRound={client.nextRound}
      onLeave={client.goHome}
      onCloseTable={client.closeRoom}
    />
  ) : (
    <LoadingView key="game-loading" message="La table se met en place…" />
  );

  return (
    <MotionConfig reducedMotion="user">
      <div className="app-root">
        <BackdropGate />
        <div className="table-texture" aria-hidden="true" />
        <AnimatePresence mode="wait">
          <motion.div
            className="screen-stage"
            key={screen.key}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {screen}
          </motion.div>
        </AnimatePresence>
        {/* L'accueil n'a pas de place pour un bouton de plus : le panneau s'ouvre d'ici. */}
        {social.snapshot !== null && !client.room && !client.isRestoring && !showAuthScreen && !showProfileScreen && !friendsOpen && (
          <motion.button
            className="friends-fab"
            type="button"
            onClick={() => openFriends(social.incoming.length > 0 ? "requests" : "friends")}
            aria-label={
              social.incoming.length > 0
                ? `Ouvrir tes amis, ${social.incoming.length} demande${social.incoming.length > 1 ? "s" : ""} en attente`
                : "Ouvrir la liste de tes amis"
            }
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: "spring", stiffness: 300, damping: 24 }}
          >
            <UsersRound size={21} aria-hidden="true" />
            {social.incoming.length > 0 && (
              <span className="friends-fab__badge" aria-hidden="true">{social.incoming.length}</span>
            )}
          </motion.button>
        )}
        <FriendsPanel
          open={friendsOpen && social.snapshot !== null}
          tab={friendsTab}
          social={social}
          canInvite={canInvite}
          onTabChange={setFriendsTab}
          onClose={() => setFriendsOpen(false)}
          onJoinFriend={(roomCode) => void joinFriendRoom(roomCode)}
        />
        <PwaInstallPrompt
          enabled={!client.room && !client.isRestoring && !showAuthScreen && !showProfileScreen}
        />
        <InviteToast
          invites={social.invites}
          inGame={client.room !== null}
          pending={social.pending}
          onAccept={(invite) => void acceptInvite(invite)}
          onDecline={(invite) => void social.respondToInvite(invite.id, false)}
        />
        <UpdateBanner
          ready={update.ready}
          autoApplies={client.room === null && !client.isRestoring}
          onApply={update.apply}
        />
        <ErrorToast
          error={client.error ?? socialFailure}
          onDismiss={client.error !== null ? client.dismissError : social.dismissError}
        />
      </div>
    </MotionConfig>
  );
}
